/**
 * Request Card — Reusable card view for any request type
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaExternalLinkAlt, FaUserTie, FaClock } from 'react-icons/fa';
import StatusBadge from './StatusBadge';

const TYPE_CONFIG = {
  leave: { label: 'Leave Request', chipClass: 'chip-leave' },
  early_leave: { label: 'Early Leave', chipClass: 'chip-early_leave' },
  gate_pass: { label: 'Gate Pass', chipClass: 'chip-gate_pass' },
  extra_work: { label: 'Extra Work', chipClass: 'chip-extra_work' },
};

const getStageName = (stage) => {
  const names = {
    submitted: 'Submitted',
    dept_head: 'Dept Head',
    production_head: 'Production Head',
    manager: 'Manager',
    hr: 'HR',
    director: 'Director',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  };
  return names[stage] || stage;
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const RequestCard = ({ request, showEmployee = false, compact = false, layout = 'grid', approverActions = null }) => {
  const navigate = useNavigate();
  const type = request.requestType || 'leave';
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.leave;

  const getDateRange = () => {
    if (type === 'leave') return `${formatDate(request.fromDate)} → ${formatDate(request.toDate)}`;
    if (type === 'early_leave') return formatDate(request.fromDate);
    if (type === 'gate_pass') return `${formatDate(request.date)} → ${formatDate(request.returnDate)}`;
    if (type === 'extra_work') return formatDate(request.workDate);
    return '—';
  };

  if (layout === 'list') {
    return (
      <div
        className={`wrms-request-card wrms-request-card-list card-${type}`}
        onClick={() => navigate(`/wrms/request/${type}/${request.id}`)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '16px 20px',
          flexWrap: 'wrap',
          marginBottom: '4px'
        }}
      >
        {/* Left Side: Type chip and ID */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1.2', minWidth: '180px' }}>
          <div>
            <span className={`wrms-type-chip ${cfg.chipClass}`} style={{ display: 'inline-block', marginBottom: '2px' }}>
              {cfg.label}
            </span>
            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>
              {request.requestId}
            </div>
          </div>
        </div>

        {/* Employee Info if showEmployee is true */}
        {/* Employee Info if showEmployee is true */}
        {showEmployee && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1.5', minWidth: '180px' }}>
            {(request.employeePhoto || request.photoURL || request.personal?.photo) ? (
              <img
                src={request.employeePhoto || request.photoURL || request.personal?.photo}
                alt=""
                className="rounded-circle shadow-sm"
                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="wrms-avatar" style={{ width: '32px', height: '32px', fontSize: '0.8rem' }}>
                {(request.employeeName || 'E')[0]}
              </div>
            )}
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#0f172a' }}>{request.employeeName}</div>
              <div style={{ fontSize: '0.65rem', color: '#64748b' }}>{request.department}</div>
            </div>
          </div>
        )}

        {/* Date Range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '2', minWidth: '180px', fontSize: '0.8rem', color: '#334155' }}>
          <FaCalendarAlt size={11} style={{ color: '#4f46e5' }} />
          <span style={{ fontWeight: 600 }}>{getDateRange()}</span>
        </div>

        {/* Current Stage */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1.5', minWidth: '140px', fontSize: '0.75rem', color: '#64748b' }}>
          <FaUserTie size={10} />
          <span>Stage: <strong style={{ color: '#0f172a' }}>{getStageName(request.currentStage)}</strong></span>
        </div>

        {/* Status & Submitted Date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#94a3b8' }}>
            <FaClock size={9} />
            <span>{formatDate(request.submittedAt)}</span>
          </div>
          <StatusBadge status={request.status} />
          {approverActions ? (
            <div className="wrms-card-actions" onClick={e => e.stopPropagation()}>
              <button
                className="wrms-btn-outline"
                onClick={() => navigate(`/wrms/request/${type}/${request.id}`)}
                style={{ padding: '4px 10px', fontSize: '0.72rem' }}
              >
                View
              </button>
              <button
                className="wrms-btn-success"
                onClick={() => approverActions.handleApprove(request)}
                style={{ padding: '4px 10px', fontSize: '0.72rem' }}
              >
                Approve
              </button>
              <button
                className="wrms-btn-danger"
                onClick={() => approverActions.handleReject(request)}
                style={{ padding: '4px 10px', fontSize: '0.72rem' }}
              >
                Reject
              </button>
            </div>
          ) : (
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4f46e5', whiteSpace: 'nowrap' }}>
              View Details <FaExternalLinkAlt size={9} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`wrms-request-card card-${type}`}
      onClick={() => navigate(`/wrms/request/${type}/${request.id}`)}
      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Header Row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className={`wrms-type-chip ${cfg.chipClass}`}>{cfg.label}</span>
        </div>
        <StatusBadge status={request.status} />
      </div>

      {/* Request ID */}
      <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b', marginBottom: '10px', fontWeight: 600 }}>
        {request.requestId}
      </div>

      {/* Employee (for approver/admin view) */}
      {showEmployee && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          {(request.employeePhoto || request.photoURL || request.personal?.photo) ? (
            <img
              src={request.employeePhoto || request.photoURL || request.personal?.photo}
              alt=""
              className="rounded-circle shadow-sm"
              style={{ width: '36px', height: '36px', objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="wrms-avatar">{(request.employeeName || 'E')[0]}</div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a' }}>{request.employeeName}</div>
            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{request.designation} · {request.department}</div>
          </div>
        </div>
      )}

      {/* Date Range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', fontSize: '0.82rem', color: '#334155' }}>
        <FaCalendarAlt size={11} style={{ color: '#4f46e5' }} />
        <span style={{ fontWeight: 600 }}>{getDateRange()}</span>
      </div>

      {/* Current Stage */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px', fontSize: '0.75rem', color: '#64748b' }}>
        <FaUserTie size={10} />
        <span>Stage: <strong style={{ color: '#0f172a' }}>{getStageName(request.currentStage)}</strong></span>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        paddingTop: '12px', borderTop: '1px solid #f1f5f9', marginTop: 'auto'
      }}>
        {approverActions ? (
          <div className="wrms-card-actions" onClick={e => e.stopPropagation()}>
            <button
              className="wrms-btn-outline"
              onClick={() => navigate(`/wrms/request/${type}/${request.id}`)}
              style={{ padding: '4px 8px', fontSize: '0.7rem', flex: 1, justifyContent: 'center' }}
            >
              View
            </button>
            {approverActions.handleApprove && (
              <button
                className="wrms-btn-success"
                onClick={() => approverActions.handleApprove(request)}
                style={{ padding: '4px 8px', fontSize: '0.7rem', flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '3px' }}
              >
                Approve
              </button>
            )}
            {approverActions.handleHold && (
              <button
                className="wrms-btn-warning"
                onClick={() => approverActions.handleHold(request)}
                style={{ padding: '4px 8px', fontSize: '0.7rem', flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '3px', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}
              >
                Hold
              </button>
            )}
            {approverActions.handleReject && (
              <button
                className="wrms-btn-danger"
                onClick={() => approverActions.handleReject(request)}
                style={{ padding: '4px 8px', fontSize: '0.7rem', flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: '3px' }}
              >
                Reject
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#94a3b8' }}>
              <FaClock size={9} />
              <span>{formatDate(request.submittedAt)}</span>
            </div>
            <div style={{
              fontSize: '0.72rem', fontWeight: 700, color: '#4f46e5',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}>
              View Details <FaExternalLinkAlt size={9} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestCard;
